import { Controller } from "@hotwired/stimulus"

const PARTICLE_LIFETIME_MS = 850
const POINTER_INTERVAL_MS = 32
const MAX_PARTICLES = 42

// Connects to data-controller="glitter-trail"
export default class extends Controller {
  connect() {
    this.enabled = this.shouldEnable()
    this.lastPointerAt = 0
    this.particles = new Set()
    this.removeTimers = new Map()

    if (!this.enabled) {
      return
    }

    this.container = document.createElement("div")
    this.container.className = "glitter-trail"
    this.container.setAttribute("aria-hidden", "true")
    document.body.appendChild(this.container)

    this.handlePointerMove = this.handlePointerMove.bind(this)
    this.handleBeforeCache = this.handleBeforeCache.bind(this)

    window.addEventListener("pointermove", this.handlePointerMove, { passive: true })
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
  }

  disconnect() {
    if (!this.enabled) {
      return
    }

    window.removeEventListener("pointermove", this.handlePointerMove)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    this.clearParticles()
    this.container?.remove()
  }

  shouldEnable() {
    return window.matchMedia("(min-width: 768px) and (pointer: fine)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  handlePointerMove(event) {
    if (event.pointerType && event.pointerType !== "mouse") {
      return
    }

    const now = performance.now()
    if (now - this.lastPointerAt < POINTER_INTERVAL_MS) {
      return
    }

    this.lastPointerAt = now
    this.addParticle(event.clientX, event.clientY)
  }

  addParticle(x, y) {
    if (this.particles.size >= MAX_PARTICLES) {
      const oldestParticle = this.particles.values().next().value
      this.removeParticle(oldestParticle)
    }

    const particle = document.createElement("span")
    const size = this.randomBetween(6, 15)
    const driftX = this.randomBetween(-18, 18)
    const driftY = this.randomBetween(-26, 10)
    const rotate = this.randomBetween(-90, 90)

    particle.className = `glitter-trail__particle glitter-trail__particle--${Math.random() > 0.48 ? "star" : "dust"}`
    particle.style.left = `${x}px`
    particle.style.top = `${y}px`
    particle.style.width = `${size}px`
    particle.style.height = `${size}px`
    particle.style.setProperty("--sparkle-x", `${driftX}px`)
    particle.style.setProperty("--sparkle-y", `${driftY}px`)
    particle.style.setProperty("--sparkle-rotate", `${rotate}deg`)

    this.container.appendChild(particle)
    this.particles.add(particle)

    const timer = window.setTimeout(() => {
      this.removeParticle(particle)
    }, PARTICLE_LIFETIME_MS)
    this.removeTimers.set(particle, timer)
  }

  removeParticle(particle) {
    if (!particle) {
      return
    }

    window.clearTimeout(this.removeTimers.get(particle))
    this.removeTimers.delete(particle)
    this.particles.delete(particle)
    particle.remove()
  }

  clearParticles() {
    this.particles.forEach((particle) => this.removeParticle(particle))
  }

  handleBeforeCache() {
    this.clearParticles()
    this.container?.remove()
  }

  randomBetween(min, max) {
    return Math.random() * (max - min) + min
  }
}
